import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  getRoles, getRole, getAvailablePermissions, createRole, 
  updateRole, deleteRole, duplicateRole 
} from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Shield, Plus, Edit, Trash2, Copy, Users, Lock, Unlock,
  Loader2, Search, AlertTriangle, CheckCircle, ChevronRight
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function RoleManagement() {
  const outletContext = useOutletContext() || {};
  const { tenant, user } = useAuth();
  const tenantId = outletContext?.tenantId || tenant?.id;
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // State
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [restrictedFeatures, setRestrictedFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
    restricted_features: []
  });

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getRoles(tenantId);
      setRoles(response.data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Fetch permissions
  const fetchPermissions = useCallback(async () => {
    try {
      const response = await getAvailablePermissions();
      setPermissions(response.data.permissions || []);
      setRestrictedFeatures(response.data.restricted_features || []);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, [fetchRoles, fetchPermissions]);

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: [],
      restricted_features: []
    });
    setSelectedRole(null);
  };

  // Handle create
  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a role name');
      return;
    }

    try {
      const data = {
        ...formData,
        tenant_id: tenantId
      };
      await createRole(data, user.id);
      toast.success('Role created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      console.error('Error creating role:', error);
      toast.error(error.response?.data?.detail || 'Failed to create role');
    }
  };

  // Handle edit
  const handleEdit = async () => {
    if (!selectedRole || !formData.name.trim()) return;

    try {
      await updateRole(selectedRole.id, formData);
      toast.success('Role updated successfully');
      setShowEditDialog(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedRole) return;

    try {
      await deleteRole(selectedRole.id);
      toast.success('Role deleted successfully');
      setShowDeleteDialog(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  // Handle duplicate
  const handleDuplicate = async (role) => {
    const newName = prompt('Enter name for the new role:', `${role.name} (Copy)`);
    if (!newName) return;

    try {
      await duplicateRole(role.id, newName, tenantId);
      toast.success('Role duplicated successfully');
      fetchRoles();
    } catch (error) {
      console.error('Error duplicating role:', error);
      toast.error(error.response?.data?.detail || 'Failed to duplicate role');
    }
  };

  // Open edit dialog
  const openEditDialog = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || [],
      restricted_features: role.restricted_features || []
    });
    setShowEditDialog(true);
  };

  // Open delete dialog
  const openDeleteDialog = (role) => {
    setSelectedRole(role);
    setShowDeleteDialog(true);
  };

  // Toggle permission
  const togglePermission = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId]
    }));
  };

  // Toggle restricted feature
  const toggleRestrictedFeature = (featureId) => {
    setFormData(prev => ({
      ...prev,
      restricted_features: prev.restricted_features.includes(featureId)
        ? prev.restricted_features.filter(f => f !== featureId)
        : [...prev.restricted_features, featureId]
    }));
  };

  // Group permissions by category
  const permissionsByCategory = permissions.reduce((acc, perm) => {
    const cat = perm.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(perm);
    return acc;
  }, {});

  // Filter roles
  const filteredRoles = roles.filter(role => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return role.name.toLowerCase().includes(search) || 
             role.description?.toLowerCase().includes(search);
    }
    return true;
  });

  // Separate system and custom roles
  const systemRoles = filteredRoles.filter(r => r.is_system_role);
  const customRoles = filteredRoles.filter(r => !r.is_system_role);

  // Render permission selector
  const renderPermissionSelector = () => (
    <div className="space-y-4">
      <Label className={isDark ? 'text-white' : ''}>Permissions</Label>
      <ScrollArea className="h-[300px] pr-4">
        {Object.entries(permissionsByCategory).map(([category, perms]) => (
          <div key={category} className="mb-4">
            <h4 className={`font-medium mb-2 capitalize ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {category}
            </h4>
            <div className="space-y-2">
              {perms.map(perm => (
                <div 
                  key={perm.id}
                  className={`flex items-center gap-3 p-2 rounded cursor-pointer ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                  onClick={() => togglePermission(perm.id)}
                >
                  <Checkbox 
                    checked={formData.permissions.includes(perm.id) || formData.permissions.includes('all')}
                    disabled={formData.permissions.includes('all') && perm.id !== 'all'}
                  />
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : ''}`}>{perm.name}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{perm.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </ScrollArea>

      <Label className={isDark ? 'text-white' : ''}>Restricted Features</Label>
      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        Features that this role cannot access even if they have admin permissions
      </p>
      <div className="space-y-2">
        {restrictedFeatures.map(feature => (
          <div 
            key={feature.id}
            className={`flex items-center gap-3 p-2 rounded cursor-pointer ${
              isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
            }`}
            onClick={() => toggleRestrictedFeature(feature.id)}
          >
            <Checkbox checked={formData.restricted_features.includes(feature.id)} />
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-white' : ''}`}>{feature.name}</p>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{feature.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className={`p-6 space-y-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Role Management
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Create and manage custom roles with specific permissions
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-role-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Role
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search roles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`pl-10 ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}
        />
      </div>

      {/* System Roles */}
      <div>
        <h2 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
          <Lock className="h-5 w-5 text-blue-500" />
          System Roles
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {systemRoles.map(role => (
            <Card key={role.id} className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-500" />
                    <CardTitle className={`text-lg ${isDark ? 'text-white' : ''}`}>{role.name}</CardTitle>
                  </div>
                  <Badge variant="outline" className="text-blue-500 border-blue-500">System</Badge>
                </div>
                <CardDescription className={isDark ? 'text-gray-400' : ''}>
                  {role.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm mb-2">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    {role.permissions?.includes('all') 
                      ? 'Full Access' 
                      : `${role.permissions?.length || 0} permissions`}
                  </span>
                </div>
                {role.restricted_features?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {role.restricted_features.map(f => (
                      <Badge key={f} variant="outline" className="text-xs text-red-500 border-red-500">
                        <Lock className="h-3 w-3 mr-1" />
                        {f.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="mt-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDuplicate(role)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicate
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Custom Roles */}
      <div>
        <h2 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
          <Unlock className="h-5 w-5 text-green-500" />
          Custom Roles ({customRoles.length})
        </h2>
        
        {customRoles.length === 0 ? (
          <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
            <CardContent className="py-8 text-center">
              <Shield className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                No custom roles created yet. Create a role to get started.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {customRoles.map(role => (
              <Card key={role.id} className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-green-500" />
                      <CardTitle className={`text-lg ${isDark ? 'text-white' : ''}`}>{role.name}</CardTitle>
                    </div>
                    <Badge className="bg-green-100 text-green-700">Custom</Badge>
                  </div>
                  <CardDescription className={isDark ? 'text-gray-400' : ''}>
                    {role.description || 'No description'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                      {role.permissions?.includes('all') 
                        ? 'Full Access' 
                        : `${role.permissions?.length || 0} permissions`}
                    </span>
                  </div>
                  {role.restricted_features?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {role.restricted_features.map(f => (
                        <Badge key={f} variant="outline" className="text-xs text-red-500 border-red-500">
                          <Lock className="h-3 w-3 mr-1" />
                          {f.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-3">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openEditDialog(role)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDuplicate(role)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-500 hover:text-red-600"
                      onClick={() => openDeleteDialog(role)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Create Custom Role</DialogTitle>
            <DialogDescription>
              Create a new role with specific permissions
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className={isDark ? 'text-white' : ''}>Role Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Senior Auditor"
                className={isDark ? 'bg-gray-700 border-gray-600' : ''}
                data-testid="role-name-input"
              />
            </div>
            <div>
              <Label className={isDark ? 'text-white' : ''}>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe what this role can do..."
                rows={2}
                className={isDark ? 'bg-gray-700 border-gray-600' : ''}
              />
            </div>

            {renderPermissionSelector()}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} data-testid="create-role-submit">
              Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Edit Role</DialogTitle>
            <DialogDescription>
              Modify role permissions
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className={isDark ? 'text-white' : ''}>Role Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className={isDark ? 'bg-gray-700 border-gray-600' : ''}
              />
            </div>
            <div>
              <Label className={isDark ? 'text-white' : ''}>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={2}
                className={isDark ? 'bg-gray-700 border-gray-600' : ''}
              />
            </div>

            {renderPermissionSelector()}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Delete Role?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the role "{selectedRole?.name}"? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <Alert className={isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}>
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <AlertDescription className={isDark ? 'text-red-200' : 'text-red-800'}>
              Users assigned to this role will need to be reassigned to another role.
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
